import { EntityTypes, UserRightsEnum } from '../../utilities/Constants';
import { IExpertCreateModel, IGeneralOpinion } from '../../interfaces/IExpert';

import Authorization from '../../stores/Authorization';
import { CheckmateDialog } from '../../components/shared/dialog';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { DocumentsList } from '../../components/documents-list/DocumentsList';
import { ExpertDetailForm } from '../../components/expert/ExpertDetailForm';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { PageWithAffixedHeader } from '../../shared/PageWithAffixedHeader';
import { showErrorToast } from '../../shared/ToastUtils';
import { useAuthContext } from '../../contexts/AuthContext';
import { useExpertCreateMutation } from './useExpertCreateMutation';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useZoneContext } from '../../contexts/useZoneContext';

export function ExpertCreate() {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();

    const authorizedToCreateExpert = Authorization.userHasRight(
        UserRightsEnum.AddExpert,
        user,
        zoneContext.zoneGuid
    );

    const [showDuplicateExpertMsg, setShowDuplicateExpertMsg] = useState<boolean>(false);
    const [formData, setFormData] = useState<IExpertCreateModel>();
    const [error, setError] = useState<string>();
    const [validation, setValidation] = useState<IValidation>({});

    const createMutation = useExpertCreateMutation((errorMessage: string) =>
        setError(errorMessage)
    );

    const handleSaveClick = async (onSuccess?: (epxertGuid: string) => void) => {
        const validation: IValidation = {};
        let hasErrors = false;

        if (!formData?.lastName) {
            validation.lastName = ['Required'];
            hasErrors = true;
        }

        if (!formData?.firstName) {
            validation.firstName = ['Required'];
            hasErrors = true;
        }

        /*if (!formData?.credentials) {
            validation.credentials = ['Required'];
            hasErrors = true;
        }*/

        if (!formData?.expertTypeId) {
            validation.type = ['Required'];
            hasErrors = true;
        }

        if (!formData?.disciplines?.length) {
            validation.disciplines = ['Required'];
            hasErrors = true;
        }

        setValidation(validation);

        if (hasErrors) {
            return;
        }

        const expertLastName = formData?.lastName ?? '';
        const response = await zoneContext.zoneApiClient.isExpertNameExists(expertLastName, '');
        if (response.errorMessage) {
            if (response.errors !== undefined && response.errors[0] !== undefined) {
                setError(`Failed to create Expert : ${response.errors[0].message}`);
            }
            return;
        }

        if (response?.httpResponse.ok) {
            //if expert Last Name is duplicate then show the warning message.
            if (response.payload === true) {
                setShowDuplicateExpertMsg(true);
            } else {
                //other wise Create the New expert.
                createExpert(onSuccess);
            }
        }
    };

    const handleCancelClick = () => {
        zoneContext.zoneNavigate(LocalRoutes.Experts);
    };

    const handleCreateEntityBeforeUpload = () => {
        const onSaveSuccess = (expertGuid: string) => {
            navigate(
                zoneContext.buildZoneUrl(LocalRoutes.ExpertDetail.replace(':id', expertGuid)),
                {
                    state: { showUpload: true },
                }
            );
        };

        handleSaveClick(onSaveSuccess);
    };

    const createExpertWithDuplicateName = async () => {
        await createExpert();
    };

    const createExpert = async (onSuccess?: (epxertGuid: string) => void) => {
        const mappedFormData: IExpertCreateModel = {
            ...formData,
            firstName: formData?.firstName ?? '',
            lastName: formData?.lastName ?? '',
            credentials: formData?.credentials ?? '',
            expertTypeId: formData?.expertTypeId ?? 0,
            disciplines: formData?.disciplines ?? [],
            generalOpinions: formData?.generalOpinions?.map(
                (go, idx) =>
                    ({
                        topic: {
                            ...go.topic,
                            correlationKeyId: idx,
                        },
                        opinion: {
                            ...go.opinion,
                            correlationKeyId: idx,
                        },
                    }) as IGeneralOpinion
            ),
        };

        createMutation
            .mutateAsync(mappedFormData!)
            .then((newExpert) => {
                if (typeof onSuccess === 'function' && newExpert) {
                    onSuccess(newExpert?.guid);
                } else {
                    zoneContext.zoneNavigate(LocalRoutes.Experts);
                }
            })
            .catch(() => {
                showErrorToast('Failed to create Expert');
            });
    };

    if (!authorizedToCreateExpert) {
        return (
            <div className="row">
                <div className="text-danger">
                    You do not have permission to perform this action.
                </div>
            </div>
        );
    }
    return (
        <>
            {createMutation.isLoading && <Loader />}
            <PageWithAffixedHeader
                headerComponent={
                    <>
                        <div className="row mb-3">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <h1>Add New Expert</h1>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-orange me-2"
                                        onClick={() => handleSaveClick()}
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={handleCancelClick}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                        {error && (
                            <div>
                                <span className="text-danger">{error}</span>
                            </div>
                        )}
                    </>
                }
            >
                <ExpertDetailForm
                    user={user}
                    onFormDataUpdate={setFormData}
                    isCreateMode
                    isEditMode
                    validation={validation}
                />
                <div className="row" id="AttachmentsDiv">
                    <DocumentsList
                        uploadOnly={true}
                        user={user}
                        entityGuid={''}
                        entityTypeId={EntityTypes.Expert}
                        title="Attachments"
                        documents={[]}
                        parentEntityJson={'{}'}
                        parentEntityRedirectUrl={LocalRoutes.ExpertDetail.replace(':id', '')}
                        authorizedToEdit={Authorization.userHasRight(
                            UserRightsEnum.UpdateExpert,
                            user,
                            zoneContext.zoneGuid
                        )}
                        onCreateEntityBeforeUpload={handleCreateEntityBeforeUpload}
                        documentTypeId={DocumentTypeEnum.CaseExpert}
                    />
                </div>
            </PageWithAffixedHeader>

            <CheckmateDialog
                isShowingModal={showDuplicateExpertMsg}
                body="An expert with the same last name already exists. Do you want to continue?"
                handleClose={() => {
                    setShowDuplicateExpertMsg(false);
                }}
                handleConfirm={() => {
                    createExpertWithDuplicateName();
                    setShowDuplicateExpertMsg(false);
                }}
                confirmText="Continue"
                cancelText="Cancel"
                confirmButtonClassName="btn btn-black float-end"
                dialogClassName="confirm-document-delete-dialog"
                closeButtonClassName="btn btn-default float-end"
            />
        </>
    );
}
